import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, BarChart3, CheckCircle2, ChevronLeft, ChevronRight, Edit, Loader2, Save } from "lucide-react";
import { API, CASE_TYPES, LEVELS, DEFAULT_FORM } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { ScoreGrid, ScoreCard } from "@/components/shared/ScoreComponents";

const AnalyticsNewPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  
  const analyze = async () => {
    if (!form.title.trim()) {
      setError("Please enter a report title");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/analyze`, form);
      setResult(res.data);
      setStep(5);
    } catch (e) {
      setError("Analysis failed");
    } finally {
      setLoading(false);
    }
  };
  
  const save = async () => {
    try {
      const res = await axios.post(`${API}/reports`, { title: form.title, input_data: form, result });
      navigate(`/reports/${res.data.id}`);
    } catch (e) {
      setError("Save failed");
    }
  };
  
  const steps = ["Matter", "Evidence", "Insurance", "Procedural"];
  
  const renderFields = (fields) => (
    <div className="grid gap-4 md:grid-cols-2">
      {fields.map(([key, label, options]) => (
        <div key={key} className="space-y-1.5">
          <Label className="text-sm">{label}</Label>
          <Select value={form[key]} onValueChange={v => update(key, v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {(options || LEVELS).map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="analytics-new-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Matter Analytics</h1>
          <p className="text-muted-foreground">Generate procedural intelligence scores</p>
        </div>
        
        {step < 5 && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Step {step} of 4</span>
              <span className="font-medium">{steps[step - 1]}</span>
            </div>
            <Progress value={step * 25} className="h-1.5" />
          </div>
        )}
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <Card className="mb-6">
          <CardContent className="p-6">
            {step === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-1.5">
                  <Label>Report Title</Label>
                  <Input 
                    value={form.title} 
                    onChange={e => update('title', e.target.value)}
                    placeholder="e.g., Smith v. Jones PI Matter"
                  />
                </div>
                {renderFields([
                  ["case_type", "Case Type", CASE_TYPES],
                  ["matter_goal", "Matter Goal", [
                    { value: "evaluate", label: "Evaluate" },
                    { value: "defend", label: "Defend" },
                    { value: "prosecute", label: "Prosecute" },
                    { value: "settle", label: "Settle" }
                  ]],
                  ["venue_type", "Venue Type", [
                    { value: "small_claims", label: "Small Claims" },
                    { value: "state_superior", label: "State Superior" },
                    { value: "federal", label: "Federal" },
                    { value: "arbitration", label: "Arbitration" }
                  ]],
                  ["jury_applicable", "Jury Applicable", [
                    { value: true, label: "Yes" },
                    { value: false, label: "No" }
                  ]]
                ])}
              </div>
            )}
            
            {step === 2 && (
              <div className="animate-fade-in">
                <h3 className="text-lg font-semibold mb-4">Documentation & Evidence</h3>
                {renderFields([
                  ["photo_video_evidence", "Photo/Video Evidence"],
                  ["police_report_strength", "Police Report Strength"],
                  ["witness_quality", "Witness Quality"],
                  ["document_completeness", "Document Completeness"],
                  ["timeline_consistency", "Timeline Consistency"],
                  ["damages_clarity", "Damages Clarity"]
                ])}
              </div>
            )}
            
            {step === 3 && (
              <div className="animate-fade-in">
                <h3 className="text-lg font-semibold mb-4">Insurance & Medical</h3>
                {renderFields([
                  ["insurance_status", "Insurance Status", [
                    { value: "none", label: "None" },
                    { value: "possible", label: "Possible" },
                    { value: "confirmed", label: "Confirmed" }
                  ]],
                  ["coverage_visibility", "Coverage Visibility"],
                  ["liability_clarity", "Liability Clarity"],
                  ["medical_documentation", "Medical Documentation"],
                  ["treatment_intensity", "Treatment Intensity"]
                ])}
              </div>
            )}
            
            {step === 4 && (
              <div className="animate-fade-in">
                <h3 className="text-lg font-semibold mb-4">Procedural Environment</h3>
                {renderFields([
                  ["judge_environment", "Judge Environment", [
                    { value: "unknown", label: "Unknown" },
                    { value: "flexible", label: "Flexible" },
                    { value: "moderate", label: "Moderate" },
                    { value: "structured", label: "Structured" },
                    { value: "strict", label: "Strict" }
                  ]],
                  ["opposing_counsel_pattern", "Opposing Counsel", [
                    { value: "unknown", label: "Unknown" },
                    { value: "light_motion", label: "Light Motion" },
                    { value: "moderate_motion", label: "Moderate Motion" },
                    { value: "heavy_motion", label: "Heavy Motion" }
                  ]],
                  ["backlog_level", "Backlog Level"],
                  ["procedural_formality", "Procedural Formality"],
                  ["settlement_pressure", "Settlement Pressure"],
                  ["discovery_aggressiveness", "Discovery Aggressiveness"]
                ])}
              </div>
            )}
            
            {step === 5 && result && (
              <div className="animate-fade-in space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{form.title}</h2>
                    <p className="text-muted-foreground">Analysis Complete</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(1)}>Edit</Button>
                    <Button onClick={save}>Save Report</Button>
                  </div>
                </div>
                
                <ScoreGrid scores={result.scores} />
                
                {result.observations?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle>Observations</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {result.observations.map((obs, i) => (
                          <li key={i} className="flex gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            {obs}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
                
                {result.narrative && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Report Analysis</CardTitle>
                      <CardDescription>Generated analysis based on structured inputs</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm whitespace-pre-wrap text-muted-foreground">{result.narrative}</div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {step < 5 && (
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button variant="outline" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                {step < 4 ? (
                  <Button onClick={() => setStep(s => s + 1)}>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button onClick={analyze} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                    Analyze
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </main>
  );
};


export default AnalyticsNewPage;
